import { useLiveQuery } from 'dexie-react-hooks'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { construireJustificatif } from '../../analysis/justificatif'
import { lirePieces, listerPieces } from '../../db/pieces'
import { formatMontant } from '../../domain/argent'
import { anneeDe, aujourdhui, formatJour } from '../../domain/temps'
import { Carte, Champ, Encart, Entete, IconeDocument, Montant, Puce, SelectTiers, remettreFichier, useAction } from '../composants'
import { useDonnees } from '../donnees'

export function PageJustificatif() {
  const { d, ix, reglages } = useDonnees()
  const [params] = useSearchParams()
  const jour = aujourdhui()
  const personnes = d.tiers.filter((t) => t.type === 'personne' && !t.archive)

  const [tiersId, setTiersId] = useState<string | null>(params.get('tiers') ?? personnes[0]?.id ?? null)
  const [du, setDu] = useState(params.get('du') ?? `${anneeDe(jour)}-01-01`)
  const [au, setAu] = useState(params.get('au') ?? jour)
  const [inclureSoldees, setInclureSoldees] = useState(false)
  const [joindre, setJoindre] = useState(true)
  const [resultat, setResultat] = useState<string | null>(null)
  const { erreur, enCours, executer } = useAction()

  const pieces = useLiveQuery(() => listerPieces(), []) ?? []

  const modele = useMemo(
    () =>
      tiersId
        ? construireJustificatif(ix, joindre ? pieces : [], reglages, { tiersId, du, au, inclureSoldees, dateEmission: jour })
        : null,
    [ix, pieces, joindre, reglages, tiersId, du, au, inclureSoldees, jour],
  )

  const generer = () =>
    executer(async () => {
      if (!modele) return
      setResultat(null)
      const [{ genererPdf }, blobs] = await Promise.all([import('../../pdf/document'), lirePieces(modele.annexes.map((a) => a.pieceId))])
      const pdf = await genererPdf(modele, blobs)
      const nom = `decompte-${modele.destinataire.nom.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${jour}.pdf`
      const issue = await remettreFichier(pdf, nom)
      if (issue !== 'annule') setResultat(`Décompte généré (${Math.round(pdf.size / 1024)} Ko).`)
    })

  return (
    <>
      <Entete titre="Justificatif" sous="Décompte à remettre" retour />
      <main className="contenu">
        {!reglages.emetteurNom && (
          <Encart ton="alerte">
            Votre nom et votre adresse n’apparaîtront pas en en-tête : renseignez-les dans <Link to="/reglages">Réglages</Link>, avec l’IBAN
            sur lequel recevoir les versements.
          </Encart>
        )}

        <Carte>
          <div className="pile">
            <Champ libelle="Destinataire">
              <SelectTiers tiers={d.tiers} sansMoi valeur={tiersId} onChange={setTiersId} vide="Choisir…" />
            </Champ>
            <div className="grille-2">
              <Champ libelle="Du">
                <input type="date" value={du} onChange={(e) => setDu(e.target.value)} />
              </Champ>
              <Champ libelle="Au">
                <input type="date" value={au} onChange={(e) => setAu(e.target.value)} />
              </Champ>
            </div>
            <label className="case">
              <input type="checkbox" checked={inclureSoldees} onChange={(e) => setInclureSoldees(e.target.checked)} />
              Inclure les factures déjà réglées
            </label>
            <label className="case">
              <input type="checkbox" checked={joindre} onChange={(e) => setJoindre(e.target.checked)} />
              Joindre les factures en annexe
            </label>
          </div>
        </Carte>

        {modele && (
          <Carte titre="Aperçu">
            {modele.lignes.length === 0 ? (
              <p className="tres-doux">Aucune part {inclureSoldees ? '' : 'restant due '}pour ce destinataire sur la période.</p>
            ) : (
              <div className="pile">
                {modele.lignes.map((l) => (
                  <div key={`${l.operation.id}${l.regle}`} className="part">
                    <div className="ligne ligne--espacee">
                      <Link to={`/operations/${l.operation.id}`} className="gras">{formatJour(l.date)} · {l.libelle}</Link>
                      {l.annexes.length > 0 ? <Puce ton="info">annexe {l.annexes.join(', ')}</Puce> : <Puce>sans facture</Puce>}
                    </div>
                    <div className="doux mono">
                      {formatMontant(l.total)}
                      {l.deductions.map((dd) => ` − ${formatMontant(dd.montant)} (${dd.tiersNom})`).join('')}
                      {l.deductions.length > 0 && ` = ${formatMontant(l.assiette)}`}
                    </div>
                    <div className="ligne ligne--espacee">
                      <span>{l.regle}</span>
                      <span className="gras"><Montant valeur={l.du} /></span>
                    </div>
                    {l.recu > 0 && <div className="tres-doux">déjà versé {formatMontant(l.recu)} · reste {formatMontant(l.restant)}</div>}
                  </div>
                ))}
                <hr className="separateur" />
                <div className="ligne ligne--espacee"><span>Total de sa part</span><Montant valeur={modele.totalDu} /></div>
                <div className="ligne ligne--espacee"><span>Déjà reçu</span><Montant valeur={modele.totalRecu} /></div>
                <div className="ligne ligne--espacee gras" style={{ fontSize: '1.1rem' }}><span>Solde à verser</span><Montant valeur={modele.totalRestant} /></div>
                {modele.lignes.some((l) => l.annexes.length === 0) && (
                  <Encart ton="alerte">Certaines factures n’ont pas de pièce jointe : ajoutez la photo ou le PDF depuis l’opération pour appuyer la demande.</Encart>
                )}
              </div>
            )}
          </Carte>
        )}

        {erreur && <Encart ton="danger">{erreur}</Encart>}
        {resultat && <Encart ton="ok">{resultat}</Encart>}

        <button type="button" className="btn btn--principal btn--bloc" disabled={!modele?.lignes.length || enCours} onClick={() => void generer()}>
          <IconeDocument /> {enCours ? 'Génération…' : 'Générer le PDF'}
        </button>
      </main>
    </>
  )
}
